import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Sentry } from "./lib/sentry";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { AuthProvider } from "./contexts/AuthContext";
import { initPostHog, phCapture } from "./lib/posthog";
import { hasConsented } from "./lib/consent";
import CookieConsent from "./components/CookieConsent";
import Index from "./pages/Index";
import Generate from "./pages/Generate";
import Report from "./pages/Report";
import History from "./pages/History";
import Processing from "./pages/Processing";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import AuthRedirect from "./pages/AuthRedirect";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";
import Support from "./pages/Support";
import Unsubscribe from "./pages/Unsubscribe";
import Analytics from "./components/Analytics";

const queryClient = new QueryClient();

if (hasConsented()) initPostHog();

/** Fires a manual $pageview on every route change. */
const PostHogPageview = () => {
  const location = useLocation();
  useEffect(() => {
    phCapture("$pageview" as any);
  }, [location.pathname, location.search]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Analytics />
          <PostHogPageview />
          <CookieConsent />
          <SentryRoutes>
            <Route path="/" element={<Index />} />
            <Route path="/generate" element={<Generate />} />
            <Route path="/processing/:id" element={<Processing />} />
            <Route path="/report/:id" element={<Report />} />
            <Route path="/history" element={<History />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signin" element={<Auth />} />
            <Route path="/auth" element={<AuthRedirect />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/support" element={<Support />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
